import React, { useState, useEffect, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import Select from 'react-select';
import './App.css';
import './desktop-button-fix.css';
//import TodayProducts from './components/TodayProducts.js';
import tongueIcon from './tongue.png';
import countryIcon from './country.png';
import roasterIcon from './coffee-roaster.png';
import { Analytics } from "@vercel/analytics/react"

// Google Analytics tracking function for coffee link clicks
const trackCoffeeClick = (coffee) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'coffee_link_click', {
      event_category: 'coffee_interaction',
      event_label: coffee.name,
      coffee_name: coffee.name,
      roaster: coffee.roaster,
      processing_method: coffee.processing_method || 'Unknown',
      origin: coffee.origin || 'Unknown',
      price: coffee.price,
      product_url: coffee.product_url
    });
  }
};

// Initialize Supabase clients
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey, {db: { schema: 'prd'}});


const cleanCoffeeName = (name) => {
  if (!name) return '';
  // Remove any text within curly braces at the end of the string. Necessary for Foster & Black.
  return name.replace(/\s*\{[^}]*\}\s*$/, '').trim();
};

function App() {
  const [coffeeItems, setCoffeeItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [visibleItems, setVisibleItems] = useState([]); // For infinite scroll
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [expandedSection, setExpandedSection] = useState(null); // Track which section is expanded
  const [sectionScrollPositions, setSectionScrollPositions] = useState({}); // Track scroll position for each section

  // Quiz state
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizStep, setQuizStep] = useState('welcome'); // 'welcome', 'roasters', 'processing', 'price', 'tastingNotes', 'varieties', 'varietyList', 'complete'
  const [quizAnswers, setQuizAnswers] = useState({
    roasters: [],
    processing: [],
    priceMin: null,
    priceMax: null,
    tastingNotes: [],
    varieties: []
  });
  const [slideDirection, setSlideDirection] = useState('next'); // 'next' or 'prev'
  const [shouldAnimate, setShouldAnimate] = useState(false);

  // Check if quiz should be shown on mount
  useEffect(() => {
    const dontShowQuiz = localStorage.getItem('dontShowQuiz');
    if (!dontShowQuiz) {
      setShowQuiz(true);
    }
  }, []);

  // Handle quiz step animation - reset after animation completes
  useEffect(() => {
    if (shouldAnimate) {
      const timer = setTimeout(() => setShouldAnimate(false), 300);
      return () => clearTimeout(timer);
    }
  }, [shouldAnimate]);


  // Handle escape key press to close filter menu
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isFiltersOpen) {
        setIsFiltersOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscKey);

    // Clean up event listener
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isFiltersOpen]);

  // Prevent body scrolling when filter menu is open on mobile or quiz is open
  useEffect(() => {
    if (isFiltersOpen || showQuiz) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isFiltersOpen, showQuiz]);

  const [filters, setFilters] = useState({
    roasters: [],
    origins: [],
    priceRange: [],
    freshness: 'all', // Default: All Freshness
    categories: [],
    tastingNotes: []// ← New: processing categories filter
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');

//debounce searchInput -> searchTerm
useEffect(() => {
  const t = setTimeout(() => {
    setSearchTerm(searchInput);
  }, 120);

  return () => clearTimeout(t);
}, [searchInput]);

const clearSearchImmediately = () => {
  setSearchInput('');
  setSearchTerm('');
};

  const [isMobile, setIsMobile] = useState(false);
  const [expandedCoffee, setExpandedCoffee] = useState(null);
  // Mobile search bubble state
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const mobileSearchInputRef = useRef(null);
  const mobileSearchBubbleRef = useRef(null);

const calculateItemsPerPage = (totalFilteredItems = 0) => {
  const defaultMinPerPage = 50;
  const maxPerPage = 500;

  return Math.max(defaultMinPerPage, Math.min(totalFilteredItems, maxPerPage));
};

const [itemsPerPage, setItemsPerPage] = useState(500);

  // Get unique options for filters
  const getUniqueOptions = (items, field) => {
    const unique = [...new Set(items.map(item => item[field]))].filter(Boolean);
    return unique.sort().map(item => ({ value: item, label: item }));
  };

    // Build a flat list of all processing‐category options
  const getCategoryOptions = items => {
    const unique = new Set(items.flatMap(item => item.processing_category || []));
    return Array.from(unique)
      .sort()
      .map(value => ({ value, label: value }));
  };

  // Extract and clean tasting notes into individual words
  const getTastingNoteOptions = (items) => {
    const notes = new Set();
    items.forEach(item => {
      if (item.tasting_notes) {
        // Split by comma, semicolon, ampersand, and "and"
        const words = item.tasting_notes
          .toLowerCase()
          .split(/[,;&+]|\sand\s/)
          .map(word => word.trim())
          .filter(word => word.length > 2); // Filter out very short words
        words.forEach(word => {
          // Capitalize first letter
          const cleaned = word.charAt(0).toUpperCase() + word.slice(1);
          notes.add(cleaned);
        });
      }
    });
    return Array.from(notes).sort();
  };

  // Get price range from coffee items
  const getPriceRange = (items) => {
    const prices = items
      .map(item => parseFloat((item.price || '0').replace('$', '').trim()))
      .filter(price => !isNaN(price) && price > 0);
    
    if (prices.length === 0) return { min: 0, max: 100 };
    
    return {
      min: Math.floor(Math.min(...prices)),
      max: Math.ceil(Math.max(...prices))
    };
  };

  // Get filtered options based on previous quiz selections
  const getFilteredQuizOptions = (step) => {
    let filteredCoffees = [...coffeeItems];

    // Apply roaster filter if selected
    if (quizAnswers.roasters.length > 0) {
      filteredCoffees = filteredCoffees.filter(coffee =>
        quizAnswers.roasters.includes(coffee.roaster)
      );
    }

    // Apply processing filter if selected (OR logic - any match)
    if (quizAnswers.processing.length > 0) {
      filteredCoffees = filteredCoffees.filter(coffee =>
        Array.isArray(coffee.processing_category) &&
        quizAnswers.processing.some(p => coffee.processing_category.includes(p))
      );
    }

    // Apply price filter if selected
    if (quizAnswers.priceMin !== null || quizAnswers.priceMax !== null) {
      filteredCoffees = filteredCoffees.filter(coffee => {
        const price = parseFloat((coffee.price || '0').replace('$', '').trim());
        const min = quizAnswers.priceMin || 0;
        const max = quizAnswers.priceMax || Infinity;
        return price >= min && price <= max;
      });
    }

    // Apply tasting notes filter if selected (OR logic - any match)
    // BUT NOT when we're on the tasting notes step itself
    if (step !== 'tastingNotes' && quizAnswers.tastingNotes.length > 0) {
      filteredCoffees = filteredCoffees.filter(coffee => {
        if (!coffee.tasting_notes) return false;
        const notesLower = coffee.tasting_notes.toLowerCase();
        return quizAnswers.tastingNotes.some(note => 
          notesLower.includes(note.toLowerCase())
        );
      });
    }

    // Return options based on current step
    switch (step) {
      case 'roasters':
        return getUniqueOptions(coffeeItems, 'roaster').map(o => o.value);
      
      case 'processing':
        // For processing step, don't filter by processing to avoid narrowing options
        let processingFilteredCoffees = [...coffeeItems];
        
        // Only apply roaster filter for processing options
        if (quizAnswers.roasters.length > 0) {
          processingFilteredCoffees = processingFilteredCoffees.filter(coffee =>
            quizAnswers.roasters.includes(coffee.roaster)
          );
        }
        
        return getCategoryOptions(processingFilteredCoffees).map(o => o.value);
      
      case 'price':
        return getPriceRange(filteredCoffees);
      
      case 'tastingNotes':
        // For tasting notes step, don't filter by tasting notes themselves
        // but apply roaster, processing, and price filters
        let tastingNotesFilteredCoffees = [...coffeeItems];
        
        if (quizAnswers.roasters.length > 0) {
          tastingNotesFilteredCoffees = tastingNotesFilteredCoffees.filter(coffee =>
            quizAnswers.roasters.includes(coffee.roaster)
          );
        }
        
        if (quizAnswers.processing.length > 0) {
          tastingNotesFilteredCoffees = tastingNotesFilteredCoffees.filter(coffee =>
            Array.isArray(coffee.processing_category) &&
            quizAnswers.processing.some(p => coffee.processing_category.includes(p))
          );
        }
        
        if (quizAnswers.priceMin !== null || quizAnswers.priceMax !== null) {
          tastingNotesFilteredCoffees = tastingNotesFilteredCoffees.filter(coffee => {
            const price = parseFloat((coffee.price || '0').replace('$', '').trim());
            const min = quizAnswers.priceMin || 0;
            const max = quizAnswers.priceMax || Infinity;
            return price >= min && price <= max;
          });
        }
        
        return getTastingNoteOptions(tastingNotesFilteredCoffees);
      
      case 'varieties':
      case 'varietyList':
        return getUniqueOptions(filteredCoffees, 'variety').map(o => o.value);
      
      default:
        return [];
    }
  };

  // Detect mobile viewport
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener ? mq.addEventListener('change', update) : mq.addListener(update);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', update) : mq.removeListener(update);
    };
  }, []);

  // Focus mobile search input when opened and attach outside-click handler when open
  useEffect(() => {
    if (isMobileSearchOpen && mobileSearchInputRef.current) {
      mobileSearchInputRef.current.focus();
    }

    const handleDocumentClick = (e) => {
      if (!isMobileSearchOpen) return;
      const bubbleEl = mobileSearchBubbleRef.current;
      if (!bubbleEl) return;
      const clickedInside = bubbleEl.contains(e.target);
      if (!clickedInside && String(searchInput).trim() === '') {
        setIsMobileSearchOpen(false);
      }
    };

    if (isMobileSearchOpen) {
      document.addEventListener('mousedown', handleDocumentClick);
      document.addEventListener('touchstart', handleDocumentClick, { passive: true });
    }
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('touchstart', handleDocumentClick);
    };
  }, [isMobileSearchOpen, searchInput]);

  // Prevent body scroll when mobile overlay is open
  useEffect(() => {
    if (expandedCoffee && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [expandedCoffee, isMobile]);

  // Fetch coffee data on component mount
  useEffect(() => {
    const fetchCoffeeData = async () => {
      try {
        const { data, error } = await supabase
          .from('coffee_products')
          .select('*')
          .neq('origin', 'Unknown')
          .not('origin', 'is', null)
          .not('origin', 'eq', '')
          .order('days_since_scraped', { ascending: true })
          .limit(200);

        if (error) {
          console.error('Error fetching coffee data:', error);
          return;
        }

        // Filter out problematic data
        const filteredData = data.filter(coffee => {
          const validName = coffee.name && !coffee.name.toLowerCase().includes('blend');
          const validPrice = coffee.price && coffee.price.trim() !== '';
          return validName && validPrice;
        });

        setCoffeeItems(filteredData); // No need to shuffle since we're sorting by date
      } catch (error) {
        console.error('Error:', error.message);
      }
    };

    fetchCoffeeData();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = coffeeItems;

    // Filter by Roasters
    if (filters.roasters.length) {
      filtered = filtered.filter(coffee =>
        filters.roasters.some(r => r.value === coffee.roaster)
      );
    }

    // Filter by Freshness (days_since_scraped) with three options
    if (filters.freshness === 'new') {
      // Newly Discovered: <= 3 days
      filtered = filtered.filter(coffee => Number(coffee.days_since_scraped) <= 3);
    } else if (filters.freshness === 'older') {
      // Older Finds: > 3 days
      filtered = filtered.filter(coffee => Number(coffee.days_since_scraped) > 3);
    } // 'all' does not filter


    // Filter by search term
    if (searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(coffee =>
        (coffee.name && coffee.name.toLowerCase().includes(searchLower)) ||
        (coffee.roaster && coffee.roaster.toLowerCase().includes(searchLower)) ||
        (coffee.origin && coffee.origin.toLowerCase().includes(searchLower)) ||
        (coffee.processing_method && coffee.processing_method.toLowerCase().includes(searchLower)) ||
        (coffee.variety && coffee.variety.toLowerCase().includes(searchLower))
      );
    }

    // Filter by tasting notes (any match)
    if (Array.isArray(filters.tastingNotes) && filters.tastingNotes.length) {
      const selectedNotes = filters.tastingNotes.map(n => n.toLowerCase());
      filtered = filtered.filter(coffee => {
        const raw = (coffee.tasting_notes || '').toLowerCase();
        return selectedNotes.some(note => raw.includes(note));
      });
    }

    // Filter by Processing categories (OR logic - changed from AND)
    if (filters.category) {
      filtered = filtered.filter(coffee =>
        Array.isArray(coffee.processing_category) &&
        coffee.processing_category.includes(filters.category)
      );
    }
    // Filter by one-or-more Processing categories (OR logic - any match)
    if (Array.isArray(filters.categories) && filters.categories.length) {
      filtered = filtered.filter(coffee =>
        Array.isArray(coffee.processing_category) &&
        filters.categories.some(cat => coffee.processing_category.includes(cat))
      );
    }

    // Sort by days_since_scraped (most recent first)
    filtered = filtered.sort((a, b) => a.days_since_scraped - b.days_since_scraped);

    setFilteredItems(filtered);
    // Reset visible items when filters change
    setItemsPerPage(calculateItemsPerPage(filtered.length));
    setVisibleItems(filtered.slice(0, calculateItemsPerPage(filtered.length)));

}, [filters, coffeeItems, searchTerm]);
useEffect(() => {
  setItemsPerPage(calculateItemsPerPage(filteredItems.length));
}, [filteredItems]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const scrollThreshold = 100; // pixels from bottom to trigger load

    if (scrollHeight - scrollTop - clientHeight < scrollThreshold) {
      const nextItems = filteredItems.slice(visibleItems.length, visibleItems.length + itemsPerPage);
      if (nextItems.length > 0) {
        setVisibleItems(prevItems => [...prevItems, ...nextItems]);
      }
    }
  };

  // Clear all filters and search input
  const clearFilters = () => {
    setFilters({
      roasters: [],
      origins: [],
      priceRange: [],
      freshness: 'all',
      categories: [],
        tastingNotes: []
    });
    // Also clear the search term so both desktop and mobile search inputs reset
    setSearchTerm('');
    setSearchInput('');
    // Clear expanded section
    setExpandedSection(null);
  };

  const handleQuizWelcome = (choice) => {
    if (choice === 'explore') {
      setShowQuiz(false);
    } else {
      setShouldAnimate(true);
      setQuizStep('roasters');
    }
  };

  const handleDontShowAgain = () => {
    localStorage.setItem('dontShowQuiz', 'true');
    setShowQuiz(false);
  };

  const openQuizFromNav = () => {
    setQuizStep('welcome');
    setShouldAnimate(false);
    setQuizAnswers({
      roasters: [],
      processing: [],
      priceMin: null,
      priceMax: null,
      tastingNotes: [],
      varieties: []
    });
    setShowQuiz(true);
  };

  const handleQuizNext = (step, data) => {
    setQuizAnswers(prev => ({ ...prev, ...data }));
    setShouldAnimate(true);
    setSlideDirection('next');

    const stepOrder = ['roasters', 'processing', 'price', 'tastingNotes', 'varieties'];
    const currentIndex = stepOrder.indexOf(step);

    if (step === 'varieties' && data.wantsVarieties) {
      setQuizStep('varietyList');
    } else if (currentIndex < stepOrder.length - 1) {
      setQuizStep(stepOrder[currentIndex + 1]);
    } else {
      applyQuizFilters();
    }
  };

  const handleQuizSkip = (step) => {
    setShouldAnimate(true);
    setSlideDirection('next');

    const stepOrder = ['roasters', 'processing', 'price', 'tastingNotes', 'varieties'];
    const currentIndex = stepOrder.indexOf(step);

    if (currentIndex < stepOrder.length - 1) {
      setQuizStep(stepOrder[currentIndex + 1]);
    } else {
      applyQuizFilters();
    }
  };

  // Handle quiz step animation - reset after animation completes
  useEffect(() => {
    if (shouldAnimate) {
      const timer = setTimeout(() => setShouldAnimate(false), 300);
      return () => clearTimeout(timer);
    }
  }, [shouldAnimate]);


  const applyQuizFilters = () => {
    let newFilters = { ...filters };

    // Apply roasters
    if (quizAnswers.roasters.length > 0) {
      newFilters.roasters = quizAnswers.roasters.map(r => ({ value: r, label: r }));
    }

    // Apply processing categories
    if (quizAnswers.processing.length > 0) {
      newFilters.categories = quizAnswers.processing;
    }

    // Apply price range (stored in filters for future use)
    if (quizAnswers.priceMin !== null || quizAnswers.priceMax !== null) {
      newFilters.priceRange = [quizAnswers.priceMin, quizAnswers.priceMax];
    }

    // Apply tasting notes (OR logic)
    if (quizAnswers.tastingNotes.length > 0) {
      newFilters.tastingNotes = quizAnswers.tastingNotes.map(n => n.toLowerCase());
    } else {
      newFilters.tastingNotes = [];
    }

    // Note: Tasting notes and varieties filtering would require additional filter logic
    // For now, we'll store them in the answers but not apply complex filtering

    setFilters(newFilters);
    setShowQuiz(false);
    setQuizStep('welcome');
  };

  // Helper function to capitalize first letter of each word
  const capitalizeFirstLetter = (string) => {
    if (!string) return '';

    // Special cases that should not be modified
    const specialCases = ['PNG'];
    if (specialCases.includes(string)) {
      return string;
    }

    return string
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Helper function to format price with dollar sign and two decimal places
  const formatPrice = (price) => {
    if (!price) return '';


    // Remove any existing dollar signs and trim whitespace
    let cleanPrice = price.replace('$', '').trim();

    // Convert to float and format to 2 decimal places
    let formattedPrice = parseFloat(cleanPrice).toFixed(0);

    // Add dollar sign
    return `${formattedPrice}`;
  };

  // Helper retained if needed elsewhere; map new keys to labels
  const freshnessLabel = (value) => {
    switch (value) {
      case 'new':
        return 'Newly Discovered';
      case 'older':
        return 'Older Finds';
      case 'all':
      default:
        return 'All Freshness';
    }
  };

  const computePer100g = (price, weight) => {
      if (price == null || weight == null) return null;
      const p = typeof price === 'number' ? price : parseFloat(String(price).replace('$','').trim());
      const w = typeof weight === 'number' ? weight : parseFloat(String(weight).replace(/[^\d.]/g,''));
      if (!isFinite(p) || !isFinite(w) || w <= 0) return null;
      const per100 = (p / w) * 100;
    return per100.toFixed(1); // round to 1 decimal
    };

  // Function to handle copying product URL to clipboard
  const handleCopyLink = (event, productUrl) => {
    // Prevent default behavior
    event.preventDefault();
    event.stopPropagation();

    // Get the button that was clicked
    const button = event.currentTarget;

    // Get the icons from this specific button
    const linkIcon = button.querySelector('.link-icon');
    const checkIcon = button.querySelector('.check-icon');

    navigator.clipboard.writeText(productUrl)
      .then(() => {
        // Add copied class only to this specific button
        button.classList.add('copied');

        // Hide link icon, show check icon
        if (linkIcon) linkIcon.style.display = 'none';
        if (checkIcon) checkIcon.style.display = 'block';

        // Reset after 3 seconds
        setTimeout(() => {
          button.classList.remove('copied');
          if (linkIcon) linkIcon.style.display = 'block';
          if (checkIcon) checkIcon.style.display = 'none';
        }, 3000);
      })
      .catch(err => {
        console.error('Failed to copy link: ', err);
      });
  };

  // Group coffees by processing category
  const groupCoffeesByProcessing = (coffees) => {
    const groups = {};
    coffees.forEach(coffee => {
      if (Array.isArray(coffee.processing_category) && coffee.processing_category.length > 0) {
        coffee.processing_category.forEach(category => {
          if (!groups[category]) {
            groups[category] = [];
          }
          groups[category].push(coffee);
        });
      }
    });
    return groups;
  };

  // Define section ordering
  const getSectionOrder = (groups) => {
    const priorityOrder = ['Natural', 'Washed', 'Anaerobic'];
    const sections = Object.keys(groups);
    
    // Sort: priority items first, then by count descending
    return sections.sort((a, b) => {
      const aIndex = priorityOrder.indexOf(a);
      const bIndex = priorityOrder.indexOf(b);
      
      // If both are in priority list, sort by priority
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      // If only a is in priority, a comes first
      if (aIndex !== -1) return -1;
      // If only b is in priority, b comes first
      if (bIndex !== -1) return 1;
      
      // Neither in priority, sort by count
      return groups[b].length - groups[a].length;
    });
  };

  // Handle clicking a processing method filter in sidebar
  useEffect(() => {
    // When user selects a processing category filter, expand that section
    if (filters.categories.length === 1 && !isMobile) {
      setExpandedSection(filters.categories[0]);
    } else if (filters.categories.length !== 1) {
      setExpandedSection(null);
    }
  }, [filters.categories, isMobile]);

  // Scroll handlers for sections
  const handleSectionScroll = (sectionName, direction, containerRef) => {
    if (!containerRef.current) return;
    
    const container = containerRef.current;
    const cardWidth = container.querySelector('.coffee-card')?.offsetWidth || 0;
    const gap = 8; // 0.5rem = 8px
    const scrollAmount = (cardWidth + gap) * 3; // Scroll by 3 cards
    
    if (direction === 'left') {
      container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    } else {
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const handleViewMore = (sectionName) => {
    setExpandedSection(sectionName);
    setFilters(prev => ({
      ...prev,
      categories: [sectionName]
    }));
  };

  const handleBackToDiscover = () => {
    setExpandedSection(null);
    setFilters(prev => ({
      ...prev,
      categories: []
    }));
  };

  // Component for scroll position dots
  const ScrollPositionDots = ({ containerRef, totalItems }) => {
    const [currentPage, setCurrentPage] = useState(0);
    // Responsive items per page based on screen size
    const isLaptop = window.innerWidth >= 1024 && window.innerWidth <= 1439;
    const itemsPerPage = isLaptop ? 2 : 3; // 2 cards on laptop, 3 on desktop
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

  const handleScroll = () => {
    const scrollLeft = container.scrollLeft;
    const cardWidth = container.querySelector('.coffee-card')?.offsetWidth || 0;
    const gap = 8;
    
    // Responsive items per page based on screen size
    const isLaptop = window.innerWidth >= 1024 && window.innerWidth <= 1439;
    const responsiveItemsPerPage = isLaptop ? 2 : 3;
    const pageWidth = (cardWidth + gap) * responsiveItemsPerPage;
    const page = Math.round(scrollLeft / pageWidth);
    setCurrentPage(page);
  };

      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }, [containerRef, itemsPerPage]);

    if (totalPages <= 1) return null;

    return (
      <div className="scroll-dots">
        {Array.from({ length: totalPages }).map((_, index) => (
          <div
            key={index}
            className={`scroll-dot ${index === currentPage ? 'active' : ''}`}
          />
        ))}
      </div>
    );
  };

  // Coffee card component to avoid duplication
  const CoffeeCard = ({ coffee, index }) => (
    <div
      key={coffee.id || index}
      className="coffee-card"
      role={isMobile ? 'button' : undefined}
      tabIndex={isMobile ? 0 : undefined}
      aria-expanded={isMobile && expandedCoffee && expandedCoffee.product_url === coffee.product_url ? 'true' : 'false'}
      onClick={() => { if (isMobile) setExpandedCoffee(coffee); }}
    >
      {/* NEW BADGE */}
      {coffee.days_since_scraped <= 3 && (
        <div className="new-badge">newly discovered</div>
      )}
      <a href={coffee.product_url} target="_blank" rel="noopener noreferrer"
         className="coffee-image-link"
         onClick={(e) => { 
           trackCoffeeClick(coffee);
           if (isMobile) { e.preventDefault(); setExpandedCoffee(coffee); } 
         }}
      >
        <div className="coffee-image">
          <img src={coffee.image_url} alt={coffee.name}/>
        </div>
      </a>
      <div className="coffee-info">
        <div className="coffee-info-bg">
          <a href={coffee.product_url} target="_blank" rel="noopener noreferrer"
             className="coffee-name-link"
             onClick={(e) => { 
               trackCoffeeClick(coffee);
               if (isMobile) { e.preventDefault(); setExpandedCoffee(coffee); } 
             }}
          >
            <h2>{cleanCoffeeName(coffee.name)}</h2>
          </a>
          <table className="coffee-details">
            <tbody>
            <React.Fragment>
              {/* Tasting Notes */}
              {coffee.tasting_notes && (
                <>
                  <tr>
                    <td>Tasting Notes</td>
                  </tr>
                  <tr>
                    <td className="tasting-notes">{coffee.tasting_notes}</td>
                  </tr>
                </>
              )}

              {/* Processing Method */}
              {coffee.processing_method &&
                coffee.processing_method.trim() !== '' &&
                coffee.processing_method.toLowerCase() !== 'unknown' && (
                  <>
                    <tr>
                      <td>Processing</td>
                    </tr>
                    <tr>
                      <td>{capitalizeFirstLetter(coffee.processing_method)}</td>
                    </tr>
                  </>
                )}

              {/* Origin */}
              {coffee.origin && (
                <>
                  <tr>
                    <td>Origin</td>
                  </tr>
                  <tr>
                    <td>{capitalizeFirstLetter(coffee.origin)}</td>
                  </tr>
                </>
              )}

              {/* Variety */}
              {coffee.variety && coffee.variety.toLowerCase() !== 'unknown' && (
                <>
                  <tr>
                    <td>Variety</td>
                  </tr>
                  <tr>
                    <td>{capitalizeFirstLetter(coffee.variety)}</td>
                  </tr>
                </>
              )}

            </React.Fragment>
            </tbody>
          </table>

          {/* Mobile compact summary (no headings) */}
          {isMobile && (
            <div className="mobile-compact">
              {/* Name already present above; show notes and price here */}
              {coffee.tasting_notes && (
                <div className="mobile-note-row">
                  <img className="mobile-note-icon" src={tongueIcon} alt="" aria-hidden="true" />
                  <div className="mobile-notes">{coffee.tasting_notes}</div>
                </div>
              )}
              {coffee.processing_method &&
                coffee.processing_method.trim() !== '' &&
                coffee.processing_method.toLowerCase() !== 'unknown' && (
                  <div className="mobile-note-row">
                    <img className="mobile-note-icon" src={roasterIcon} alt="" aria-hidden="true" />
                    <div className="mobile-notes">
                      {capitalizeFirstLetter(coffee.processing_method)}
                    </div>
                  </div>
                )}
              {coffee.origin && (
                <div className="mobile-note-row">
                  <img className="mobile-note-icon" src={countryIcon} alt="" aria-hidden="true" />
                  <div className="mobile-notes">
                    {capitalizeFirstLetter(coffee.origin)}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="mobile-price">
           ${formatPrice(coffee.price)}
           {computePer100g(coffee.price, coffee.weight) ? (
             <span className="price-per-100g"> (${computePer100g(coffee.price, coffee.weight)}/100g)</span>
           ) : null}
         </div>
          {!isMobile && (
            <div className="desktop-footer">
              <div className="desktop-price">
                   ${formatPrice(coffee.price)}
                   {computePer100g(coffee.price, coffee.weight) ? (
                     <span className="price-per-100g"> (${computePer100g(coffee.price, coffee.weight)}/100g)</span>
                   ) : null}
                 </div>
              <button 
                className="copy-link-button"
                onClick={(e) => handleCopyLink(e, coffee.product_url)}
                aria-label="Copy link to clipboard"
              >
                <span className="copy-link-text">Copy Link</span>
                <div className="link-icon"></div>
                <span className="check-icon" style={{ display: 'none', fontSize: '12px', fontWeight: 'bold', width: '18px' }}>✓</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Get grouped coffees for sectioned view
  const groupedCoffees = groupCoffeesByProcessing(filteredItems);
  const sectionOrder = getSectionOrder(groupedCoffees);

  // Quiz Component
  const QuizModal = () => {
    if (!showQuiz) return null;

    return (
      <>
        <QuizBackdrop />
        <QuizContent />
      </>
    );
  };

  // Separate backdrop component that never changes
  const QuizBackdrop = React.memo(() => (
    <div className="quiz-overlay">
      <div className="quiz-backdrop" onClick={() => setShowQuiz(false)}></div>
    </div>
  ));

  // Quiz content that can change without affecting backdrop
  const QuizContent = () => {
    // Get options filtered by previous selections
    const roasterOptions = quizStep === 'roasters' ?
      getFilteredQuizOptions('roasters') : [];
    const processingOptions = quizStep === 'processing' ?
      getFilteredQuizOptions('processing') : [];
    const priceRange = quizStep === 'price' ?
      getFilteredQuizOptions('price') : { min: 0, max: 100 };
    const tastingNoteOptions = quizStep === 'tastingNotes' ?
      getFilteredQuizOptions('tastingNotes') : [];
    const varietyOptions = quizStep === 'varietyList' ?
      getFilteredQuizOptions('varietyList') : [];

    return (
      <div className="quiz-modal-wrapper">
        <div className={`quiz-modal ${shouldAnimate && slideDirection === 'next' ? 'slide-next' : ''}`}>
          <button className="quiz-close" onClick={() => setShowQuiz(false)} aria-label="Close quiz">✕</button>

          {quizStep === 'welcome' && (
            <div className="quiz-content">
              <h2 className="quiz-title">Hey!</h2>
              <p className="quiz-description">
                You can explore Australia's amazing coffee here at your own pace, or if you know what you want,
                feel free to answer these few questions and we'll direct you right to some coffee right up your alley!
              </p>
              <div className="quiz-buttons">
                <button className="quiz-button quiz-button-primary" onClick={() => handleQuizWelcome('quiz')}>
                  I know what I'm about
                </button>
                <button className="quiz-button quiz-button-secondary" onClick={() => handleQuizWelcome('explore')}>
                  Let me explore, please!
                </button>
              </div>
              <button className="quiz-dont-show" onClick={handleDontShowAgain}>
                Don't show this again
              </button>
            </div>
          )}

          {quizStep === 'roasters' && (
            <QuizQuestion
              question="Do you have any specific roasters you're after today?"
              options={roasterOptions}
              selected={quizAnswers.roasters}
              onSelect={(roasters) => setQuizAnswers(prev => ({ ...prev, roasters }))}
              onNext={() => handleQuizNext('roasters', { roasters: quizAnswers.roasters })}
              onSkip={() => handleQuizSkip('roasters')}
            />
          )}

          {quizStep === 'processing' && (
            <QuizQuestion
              question="What processing methods were you interested in today?"
              options={processingOptions}
              selected={quizAnswers.processing}
              onSelect={(processing) => setQuizAnswers(prev => ({ ...prev, processing }))}
              onNext={() => handleQuizNext('processing', { processing: quizAnswers.processing })}
              onSkip={() => handleQuizSkip('processing')}
            />
          )}

          {quizStep === 'price' && (
            <QuizPriceQuestion
              priceRange={priceRange}
              selectedMin={quizAnswers.priceMin}
              selectedMax={quizAnswers.priceMax}
              onSelect={(min, max) => setQuizAnswers(prev => ({ ...prev, priceMin: min, priceMax: max }))}
              onNext={() => handleQuizNext('price', { priceMin: quizAnswers.priceMin, priceMax: quizAnswers.priceMax })}
              onSkip={() => handleQuizSkip('price')}
            />
          )}

          {quizStep === 'tastingNotes' && (
            <QuizQuestion
              question="Do any of the following tasting notes jump out at you?"
              options={tastingNoteOptions}
              selected={quizAnswers.tastingNotes}
              onSelect={(tastingNotes) => setQuizAnswers(prev => ({ ...prev, tastingNotes }))}
              onNext={() => handleQuizNext('tastingNotes', { tastingNotes: quizAnswers.tastingNotes })}
              onSkip={() => handleQuizSkip('tastingNotes')}
              scrollable={true}
            />
          )}

          {quizStep === 'varieties' && (
            <QuizYesNo
              question="Are you looking to try any specific varieties?"
              onYes={() => handleQuizNext('varieties', { wantsVarieties: true })}
              onNo={() => {
                setQuizAnswers(prev => ({ ...prev, varieties: [] }));
                applyQuizFilters();
              }}
              onSkip={() => {
                setQuizAnswers(prev => ({ ...prev, varieties: [] }));
                applyQuizFilters();
              }}
            />
          )}

          {quizStep === 'varietyList' && (
            <QuizQuestion
              question="Which varieties are you interested in?"
              options={varietyOptions}
              selected={quizAnswers.varieties}
              onSelect={(varieties) => setQuizAnswers(prev => ({ ...prev, varieties }))}
              onNext={() => {
                handleQuizNext('varietyList', { varieties: quizAnswers.varieties });
                applyQuizFilters();
              }}
              onSkip={() => {
                setQuizAnswers(prev => ({ ...prev, varieties: [] }));
                applyQuizFilters();
              }}
            />
          )}
        </div>
      </div>
    );
  };

  const QuizQuestion = React.memo(({ question, options, selected, onSelect, onNext, onSkip, scrollable = false }) => {
    const toggleOption = (option) => {
      if (selected.includes(option)) {
        onSelect(selected.filter(o => o !== option));
      } else {
        onSelect([...selected, option]);
      }
    };

    return (
      <div className="quiz-content">
        <h2 className="quiz-question">{question}</h2>
        <div className={`quiz-pills-container ${scrollable ? 'scrollable' : ''}`}>
          {options.map(option => (
            <button
              key={option}
              className={`quiz-pill ${selected.includes(option) ? 'selected' : ''}`}
              onClick={() => toggleOption(option)}
            >
              {option}
            </button>
          ))}
        </div>
        <div className="quiz-buttons">
          <button 
            className="quiz-button quiz-button-primary" 
            onClick={onNext}
            disabled={selected.length === 0}
          >
            Select
          </button>
          <button className="quiz-button quiz-button-secondary" onClick={onSkip}>
            Skip
          </button>
        </div>
      </div>
    );
  });

  const QuizPriceQuestion = React.memo(({ priceRange, selectedMin, selectedMax, onSelect, onNext, onSkip }) => {
    // Initialize with props or defaults - no useEffect needed
    const [minValue, setMinValue] = useState(selectedMin !== null ? selectedMin : priceRange.min);
    const [maxValue, setMaxValue] = useState(selectedMax !== null ? selectedMax : priceRange.max);

    // Update parent directly in handlers, not via useEffect
    const handleMinChange = (value) => {
      setMinValue(value);
      onSelect(value, maxValue);
    };

    const handleMaxChange = (value) => {
      setMaxValue(value);
      onSelect(minValue, value);
    };

    return (
      <div className="quiz-content">
        <h2 className="quiz-question">Is there a price range you're looking for?</h2>
        <div className="quiz-price-container">
          <div className="quiz-price-inputs">
            <div className="quiz-price-input-group">
              <label>Min Price</label>
              <input
                type="number"
                min={priceRange.min}
                max={maxValue}
                value={minValue}
                onChange={(e) => {
                  const value = Math.max(priceRange.min, Math.min(maxValue, parseInt(e.target.value) || priceRange.min));
                  handleMinChange(value);
                }}
                className="quiz-price-input"
              />
            </div>
            <div className="quiz-price-input-group">
              <label>Max Price</label>
              <input
                type="number"
                min={minValue}
                max={priceRange.max}
                value={maxValue}
                onChange={(e) => {
                  const value = Math.max(minValue, Math.min(priceRange.max, parseInt(e.target.value) || priceRange.max));
                  handleMaxChange(value);
                }}
                className="quiz-price-input"
              />
            </div>
          </div>
          <div className="quiz-price-display">
            ${minValue} - ${maxValue}
          </div>
        </div>
        <div className="quiz-buttons">
          <button className="quiz-button quiz-button-primary" onClick={onNext}>
            Select
          </button>
          <button className="quiz-button quiz-button-secondary" onClick={onSkip}>
            Skip
          </button>
        </div>
      </div>
    );
  });

  const QuizYesNo = React.memo(({ question, onYes, onNo, onSkip }) => {
    return (
      <div className="quiz-content">
        <h2 className="quiz-question">{question}</h2>
        <div className="quiz-buttons">
          <button className="quiz-button quiz-button-primary" onClick={onYes}>
            Yes
          </button>
          <button className="quiz-button quiz-button-secondary" onClick={onNo}>
            No
          </button>
        </div>
        <button className="quiz-skip-link" onClick={onSkip}>
          Skip this question
        </button>
      </div>
    );
  });

  return (
    <>
      <div className="App">
        <nav className="nav-bar">
          <div className="nav-brand-container">
            <div className="nav-brand">On The <span className="grind-text">Grind</span></div>
            <h3>Explore the Newest Coffees from Australia's Best Coffee Roasters</h3>
          </div>
          <button className="nav-quiz-button" onClick={openQuizFromNav} aria-label="Open preference quiz">
            <span className="quiz-icon">☕</span>
            <span className="quiz-button-text">Preference Quiz</span>
          </button>
        </nav>
        <main className="container">
          <div className="filters-section">
            {/* Mobile Search Toggle (hidden when bubble active) */}
            <button
              className={`mobile-search-toggle ${isMobileSearchOpen ? 'hidden' : ''}`}
              onClick={() => setIsMobileSearchOpen(true)}
              aria-label="Open Search"
            >
              <span className="search-icon" aria-hidden="true"></span>
            </button>
            <button 
              className={`filters-toggle ${isFiltersOpen ? 'active' : ''}`}
              onClick={() => setIsFiltersOpen(true)}
            >
              <span className="filter-icon" aria-label="Open Filters"></span>
            </button>
            {/* Backdrop for closing filters when clicking outside */}
            <div 
              className={`filters-backdrop ${isFiltersOpen ? 'active' : ''}`}
              onClick={() => setIsFiltersOpen(false)}
            ></div>
            <div className={`filters-container ${isFiltersOpen ? 'active' : ''}`}>
              <div className="filters-header">
                <div className="filters-title">Filters</div>
                <button className="close-filters" onClick={() => setIsFiltersOpen(false)}>✕</button>
              </div>
              <div className="filters-grid">
                {/* Freshness Filter */}
                <div className="filter-group">
                  <label>Freshness</label>
                  <div className="filter-options">
                    {[
                      { value: 'all', label: 'All Freshness' },
                      { value: 'new', label: 'Newly Discovered' },
                      { value: 'older', label: 'Older Finds' },
                    ].map((option) => (
                      <label key={option.value} className="filter-option">
                        <input
                          type="radio"
                          name="freshness"
                          value={option.value}
                          checked={filters.freshness === option.value}
                          onChange={() =>
                            setFilters((prevFilters) => ({
                              ...prevFilters,
                              freshness: option.value,
                            }))
                          }
                        />
                        {option.label}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="filter-group">
                  <label>Processing</label>
                  <div className="filter-options">
                    {getCategoryOptions(coffeeItems).map(opt => (
                      <label key={opt.value} className="filter-option">
                        <input
                          type="checkbox"
                          name="processing"
                          value={opt.value}
                          checked={(Array.isArray(filters.categories) ? filters.categories : []).includes(opt.value)}
                          onChange={e => {
                            const { checked, value } = e.target;
                            setFilters(prev => ({
                              ...prev,
                              categories: checked
                                ? [...prev.categories, value]
                                : prev.categories.filter(c => c !== value)
                            }));
                          }}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>
                {/* Roaster Filter - now positioned second */}
                <div className="filter-group">
                  <label>Roaster</label>
                  <div className="filter-options">
                    <label className="filter-option">
                      <input
                        type="checkbox"
                        checked={filters.roasters.length === getUniqueOptions(coffeeItems, "roaster").length && getUniqueOptions(coffeeItems, "roaster").length > 0}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          const allRoasters = getUniqueOptions(coffeeItems, "roaster");
                          setFilters((prevFilters) => ({
                            ...prevFilters,
                            roasters: isChecked ? allRoasters : [],
                          }));
                        }}
                      />
                      Select All
                    </label>
                    {getUniqueOptions(coffeeItems, "roaster").map((option) => (
                        <label key={option.value} className="filter-option">
                          <input
                              type="checkbox"
                              value={option.value}
                              checked={filters.roasters.some(
                                  (roaster) => roaster.value === option.value
                              )}
                              onChange={(e) => {
                                const isChecked = e.target.checked;
                                setFilters((prevFilters) => ({
                                  ...prevFilters,
                                  roasters: isChecked
                                      ? [...prevFilters.roasters, option]
                                      : prevFilters.roasters.filter(
                                          (roaster) => roaster.value !== option.value
                                      ),
                                }));
                              }}
                          />
                          {option.label}
                        </label>
                    ))}
                  </div>
                </div>
              </div>
              <button onClick={clearFilters} className="clear-filters">
                Clear filters
              </button>
            </div>
          </div>
          <div className="coffee-container">
            {/* Mobile search bubble */}
            {isMobile && isMobileSearchOpen && (
              <div className="mobile-search-bubble" role="search" ref={mobileSearchBubbleRef}>
                <input
                  ref={mobileSearchInputRef}
                  type="text"
                  className="mobile-search-input"
                  placeholder="Search coffees..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setIsMobileSearchOpen(false);
                    }
                    if (e.key === 'Enter') {
                        setSearchTerm(searchInput);
                    }
                  }}
                  aria-label="Search coffees"
                />
                <button
                      className="mobile-search-clear"
                      onClick={() => { clearSearchImmediately(); setIsMobileSearchOpen(false); }}
                      aria-label="Close search"
                      title="Close search"
                    >
                      ✕
                    </button>
              </div>
            )}

          {/* Coffee Grid Header containing results info and view toggle */}
          <div className="coffee-grid-header">
            <div className={`results-info`}>
              <div className="count-info">
                {expandedSection 
                  ? `${filteredItems.length} ${expandedSection} coffee${filteredItems.length !== 1 ? 's' : ''}`
                  : `Showing ${filteredItems.length} coffee${filteredItems.length !== 1 ? 's' : ''}`
                }
              </div>
            </div>
            <div className="search-container">
              <input
                  type="text"
                  className="coffee-search-input"
                  placeholder="Search for coffee here..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
            </div>
          </div>

            {/* Desktop: Show sectioned view or expanded view */}
            {!isMobile && !expandedSection && (
              <div className="coffee-sections-container" aria-live="polite">
                {sectionOrder.length > 0 ? (
                  sectionOrder.map(sectionName => {
                    const sectionCoffees = groupedCoffees[sectionName];

                    return (
                      <CoffeeSection
                        key={sectionName}
                        sectionName={sectionName}
                        coffees={sectionCoffees}
                        onViewMore={handleViewMore}
                        onScroll={handleSectionScroll}
                        CoffeeCard={CoffeeCard}
                      />
                    );
                  })
                ) : (
                  <div className="no-items-message" role="status" aria-live="polite">
                    <p>Sorry! We can't find any coffees with those filters. Try something else!</p>
                    <button onClick={clearFilters} className="clear-filters-button" aria-label="Clear all filters">
                      Clear filters
                    </button>
                  </div>
                )}
              </div>
            )}


            {/* Desktop: Expanded single section view */}
            {!isMobile && expandedSection && (
              <>
                <button 
                  className="back-to-discover"
                  onClick={handleBackToDiscover}
                  aria-label="Back to discover view"
                >
                  ← Back to Discover
                </button>
                <div className="coffee-grid" onScroll={handleScroll} aria-live="polite">
                  {visibleItems.length > 0 ? (
                    visibleItems.map((coffee, index) => (
                      <CoffeeCard key={coffee.id || index} coffee={coffee} index={index} />
                    ))
                  ) : (
                    <div className="no-items-message" role="status" aria-live="polite">
                      <p>Sorry! We can't find any coffees with those filters. Try something else!</p>
                      <button onClick={clearFilters} className="clear-filters-button" aria-label="Clear all filters">
                        Clear filters
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Mobile: Traditional grid view (unchanged) */}
            {isMobile && (
              <div className="coffee-grid" onScroll={handleScroll} aria-live="polite">
                {visibleItems.length > 0 ? (
                  visibleItems.map((coffee, index) => (
                    <CoffeeCard key={coffee.id || index} coffee={coffee} index={index} />
                  ))
                ) : (
                  <div className="no-items-message" role="status" aria-live="polite">
                    <p>Sorry! We can't find any coffees with those filters. Try something else!</p>
                    <button onClick={clearFilters} className="clear-filters-button" aria-label="Clear all filters">
                      Clear filters
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Mobile Expanded Overlay */}
            {isMobile && expandedCoffee && (
              <div
                className="mobile-overlay"
                role="dialog"
                aria-modal="true"
                onClick={(e) => {
                  // Close when clicking outside the content (backdrop)
                  if (e.target.classList.contains('mobile-overlay')) {
                    setExpandedCoffee(null);
                  }
                }}
              >
                <div className="overlay-content">
                  <button
                    className="overlay-close"
                    onClick={() => setExpandedCoffee(null)}
                    aria-label="Close details"
                  >
                    ✕
                  </button>
                  <div className="overlay-image"><img src={expandedCoffee.image_url} alt={expandedCoffee.name} /></div>
                  <div className="overlay-body">
                    <h2 className="overlay-title">{cleanCoffeeName(expandedCoffee.name)}</h2>
                    <div className="overlay-section">
                      <h3>Price</h3>
                      <p className="overlay-price">
                        ${formatPrice(expandedCoffee.price)}
                        {computePer100g(expandedCoffee.price, expandedCoffee.weight) && (
                          <span className="price-per-100g">
                            (${computePer100g(expandedCoffee.price, expandedCoffee.weight)}/100g)
                          </span>
                        )}
                      </p>
                    </div>
                    {expandedCoffee.tasting_notes && (
                      <div className="overlay-section">
                        <h3>Tasting Notes</h3>
                        <p>{expandedCoffee.tasting_notes}</p>
                      </div>
                    )}
                    {expandedCoffee.processing_method && expandedCoffee.processing_method.trim() !== '' && expandedCoffee.processing_method.toLowerCase() !== 'unknown' && (
                      <div className="overlay-section">
                        <h3>Processing</h3>
                        <p>{capitalizeFirstLetter(expandedCoffee.processing_method)}</p>
                      </div>
                    )}
                    {expandedCoffee.origin && (
                      <div className="overlay-section">
                        <h3>Origin</h3>
                        <p>{capitalizeFirstLetter(expandedCoffee.origin)}</p>
                      </div>
                    )}
                    {expandedCoffee.variety && expandedCoffee.variety.toLowerCase() !== 'unknown' && (
                      <div className="overlay-section">
                        <h3>Variety</h3>
                        <p>{capitalizeFirstLetter(expandedCoffee.variety)}</p>
                      </div>
                    )}
                      <div className= "overlay-linkwrap">
                    <a href={expandedCoffee.product_url} target="_blank" rel="noopener noreferrer" className="overlay-link" aria-label="View on seller website" onClick={() => trackCoffeeClick(expandedCoffee)}>View On Seller's Website</a>
                      </div>
                  </div>
                </div>
              </div>
            )}


          </div>
        </main>

        {/* Quiz Modal */}
        <QuizModal />
      </div>
    <Analytics />
    </>
  );
}

// Coffee Section Component
function CoffeeSection({ sectionName, coffees, onViewMore, onScroll, CoffeeCard }) {
  const containerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);

  // Responsive items per page based on screen size
  const isLaptop = window.innerWidth >= 1024 && window.innerWidth <= 1439;
  const itemsPerPage = isLaptop ? 2 : 3; // 2 cards on laptop, 3 on desktop
  const totalPages = Math.ceil(coffees.length / itemsPerPage);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateArrows = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
      
      // Update current page for dots
      const cardWidth = container.querySelector('.coffee-card')?.offsetWidth || 0;
      const gap = 8;
      const pageWidth = (cardWidth + gap) * itemsPerPage;
      const page = Math.round(scrollLeft / pageWidth);
      setCurrentPage(page);
    };

    updateArrows();
    container.addEventListener('scroll', updateArrows);
    window.addEventListener('resize', updateArrows);

    return () => {
      container.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, [coffees.length, itemsPerPage]);

  const handleScroll = (direction) => {
    if (!containerRef.current) return;
    
    const container = containerRef.current;
    const cardWidth = container.querySelector('.coffee-card')?.offsetWidth || 0;
    const gap = 8;
    
    // Responsive scroll distance based on screen size
    const isLaptop = window.innerWidth >= 1024 && window.innerWidth <= 1439;
    const cardsToScroll = isLaptop ? 2 : 3; // Scroll 2 cards on laptop, 3 on desktop
    const scrollAmount = (cardWidth + gap) * cardsToScroll;
    
    if (direction === 'left') {
      container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    } else {
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <div className="coffee-section">
      <div className="section-header">
        <h2 className="section-title">
          {sectionName} <span className="section-count">({coffees.length})</span>
        </h2>
        <button 
          className="view-more-button"
          onClick={() => onViewMore(sectionName)}
          aria-label={`View all ${sectionName} coffees`}
        >
          View More →
        </button>
      </div>
      <div className="section-content-wrapper">
        {showLeftArrow && (
          <button 
            className="scroll-arrow scroll-arrow-left"
            onClick={() => handleScroll('left')}
            aria-label="Scroll left"
          >
            ‹
          </button>
        )}
        <div className="section-scroll-container" ref={containerRef}>
          {coffees.map((coffee, index) => (
            <CoffeeCard key={coffee.id || index} coffee={coffee} index={index} />
          ))}
        </div>
        {showRightArrow && (
          <button 
            className="scroll-arrow scroll-arrow-right"
            onClick={() => handleScroll('right')}
            aria-label="Scroll right"
          >
            ›
          </button>
        )}
      </div>
      {totalPages > 1 && (
        <div className="scroll-dots">
          {Array.from({ length: totalPages }).map((_, index) => (
            <div
              key={index}
              className={`scroll-dot ${index === currentPage ? 'active' : ''}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default App;